/**
 * Core orchestration hook for the Schedule Interview wizard.
 *
 * Three pre-fill paths — all handled by useWizardInitialValues:
 *
 *   1. Adhoc (adhoc / self-schedule-adhoc)
 *      No API. payloadRef seeded with ADHOC_PAYLOAD_DEFAULTS.
 *
 *   2. Create (manual / self-schedule)
 *      GET /ats_interviews/new → hydratePlanDefaults → payloadRef (all steps).
 *
 *   3. Edit (any mode)
 *      GET /ats_interviews/:id or /ats_schedules/:id
 *      → hydratePayloadFromApi → payloadRef (all steps).
 */
import { useCallback, useMemo } from 'react';
import { useEditConfirmation } from './editMode';
import type { TabItem } from '@kula-ai/components';
import { useStepValidation } from '@/hooks/useStepValidation';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
  ScheduleModeConfig,
} from './config/types';
import { isSelfScheduleMode } from './config/modeClassifiers';
import { MODE_CONFIGS } from './config/modes';
import { SCHEDULE_INTERVIEW_COPY } from './config/copy';
import { useWizardInitialValues } from './initialValues/useWizardInitialValues';
import { useStepRefs } from './hooks/useStepRefs';
import { useScheduleSave, type PhoneValue } from './hooks/useScheduleSave';

interface UseScheduleInterviewOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  applicationId?: number;
  candidateId?: number;
  jobId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  scheduleId?: number;
  /**
   * Candidate's current phone-on-record (from applicationData.contact).
   * Used to detect whether the user edited the phone on the Details
   * step so we can fire `PUT /api/v1/contacts/:id` only when it
   * actually changed — a view of the existing number shouldn't
   * generate a contact update.
   */
  initialCandidatePhone?: PhoneValue;
  onClose?: () => void;
  onSuccess?: () => void;
}

export function useScheduleInterview({
  mode,
  apiReference,
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  interviewId,
  scheduleId,
  initialCandidatePhone,
  onClose,
  onSuccess,
}: UseScheduleInterviewOptions) {
  const isEdit = apiReference === 'edit';
  const isSelfSchedule = isSelfScheduleMode(mode);

  // Edit is a flat experience — flip stepper configs to tabs so users
  // can jump directly between steps without enforced progression.
  const baseConfig: ScheduleModeConfig = MODE_CONFIGS[mode];
  const config: ScheduleModeConfig =
    isEdit && baseConfig.navigationStyle === 'stepper'
      ? { ...baseConfig, navigationStyle: 'tabs' }
      : baseConfig;

  // ---- Pre-fill: all three paths in one hook ----
  const {
    payloadRef,
    prefillVersion,
    interviewPlan,
    showCreateLoader,
    showEditLoader,
    showEditError,
  } = useWizardInitialValues({
    mode,
    apiReference,
    stageId,
    stageActivityId,
    candidateId,
    interviewId,
    scheduleId,
  });

  // ---- Per-step form refs ----
  const { stepRefsMap, getStepRef, stepRefs } = useStepRefs(config.steps);

  // ---- Save logic (all four mutation paths) ----
  const { handleComplete, isSaving } = useScheduleSave({
    mode,
    isEdit,
    isSelfSchedule,
    applicationId,
    candidateId,
    jobId,
    stageId,
    stageActivityId,
    interviewId,
    scheduleId,
    initialCandidatePhone,
    onClose,
    onSuccess,
  });

  // ---- Change-tracking confirmation (direct edits only) ----
  const {
    requestSave,
    confirmDialog,
    cancelDialog,
    dialogVariant,
    isDialogOpen,
    dialogFlags,
    dialogInterviewers,
  } = useEditConfirmation({
    payloadRef,
    prefillVersion,
    isEdit,
    isSelfSchedule,
  });

  // ---- Tab items ----
  const tabItems = useMemo(
    (): TabItem[] =>
      config.steps.map((step) => ({ label: step.label, value: step.key })),
    [config.steps]
  );

  // ---- Step validation orchestration ----
  const finalActionLabel = isEdit
    ? SCHEDULE_INTERVIEW_COPY.footer.saveInterview
    : SCHEDULE_INTERVIEW_COPY.footer.scheduleInterview;

  const onComplete = useCallback(
    async (payload: ScheduleInterviewPayload) => {
      requestSave((flags) => void handleComplete(payload, flags));
    },
    [requestSave, handleComplete]
  );

  const stepper = useStepValidation<ScheduleInterviewPayload>({
    tabItems,
    payloadRef,
    stepRefs,
    onComplete,
    finalActionLabel,
    nextPrefix: 'Next:',
    backPrefix: 'Back:',
    enableBack: true,
    wizardCompleteMode: 'validateAllSteps',
    activateFirstInvalidStepOnWizardComplete: true,
  });

  // ---- "Schedule interview now" secondary CTA ----
  // Validates every step in order, syncs to payload, then submits.
  // Kept here because it composes stepRefsMap + stepper.onStepChange
  // which are both available only at this orchestration level.
  const scheduleNow = useCallback(async () => {
    for (const step of config.steps) {
      const stepApi = stepRefsMap.current[step.key]?.current;
      if (!stepApi) continue;
      const ok = await stepApi.validate();
      if (!ok) {
        stepper.onStepChange(step.key);
        return;
      }
      stepApi.syncToPayload();
    }
    requestSave((flags) => void handleComplete(payloadRef.current, flags));
  }, [
    config.steps,
    handleComplete,
    payloadRef,
    requestSave,
    stepRefsMap,
    stepper,
  ]);

  return {
    config,
    payloadRef,
    getStepRef,
    isSaving,
    scheduleNow,
    prefillVersion,
    interviewPlan,
    showEditLoader,
    showEditError,
    showCreateLoader,
    // Confirmation dialog state (for direct interview edits)
    dialogVariant,
    isDialogOpen,
    dialogFlags,
    dialogInterviewers,
    confirmDialog,
    cancelDialog,
    // From useStepValidation
    tabItems: stepper.tabItems,
    activeStep: stepper.activeStep,
    isBusy: stepper.isBusy,
    onStepChange: stepper.onStepChange,
    goNextOrComplete: stepper.goNextOrComplete,
    goBack: stepper.goBack,
    canGoBack: stepper.canGoBack,
    primaryButtonLabel: stepper.primaryButtonLabel,
    backButtonLabel: stepper.backButtonLabel,
    /** Last step key — callers use this to hide the "Update interview"
     *  secondary CTA on the final step (primary CTA already saves there). */
    lastStepKey: config.steps[config.steps.length - 1]?.key,
  };
}
